import { useState } from 'react';
import './RegisterPage.css';

const translations = {
  en: {
    pageTitle: 'Create Account',
    pageSubtitle: 'Join Permata Harapan School Community',
    fullNameLabel: 'Full Name',
    fullNamePlaceholder: 'Enter your full name',
    emailLabel: 'Email Address',
    emailPlaceholder: 'Enter your email address',
    phoneLabel: 'Phone Number (Indonesia)',
    phonePlaceholder: 'e.g., 08123456789',
    passwordLabel: 'Password',
    passwordPlaceholder: 'Create a secure password (min 8 characters)',
    confirmPasswordLabel: 'Confirm Password',
    confirmPasswordPlaceholder: 'Confirm your password',
    registerButton: 'Create Account',
    registeringButton: 'Creating Account...',
    loginLink: 'Already have an account? Sign in',
    backToHome: 'Back to Homepage',
    successTitle: 'Account Created Successfully!',
    successMessage: 'Your account has been created. You can now sign in to access the admissions portal.',
    signInButton: 'Sign In Now',
    errorTitle: 'Registration Failed',
    passwordMismatch: 'Passwords do not match',
    passwordTooShort: 'Password must be at least 8 characters long',
    invalidEmail: 'Please enter a valid email address',
    invalidPhone: 'Phone number must be 10-13 digits (Indonesia format: 08xxx)',
    requiredFields: 'Please fill in all required fields',
  },
  id: {
    pageTitle: 'Buat Akun',
    pageSubtitle: 'Bergabung dengan Komunitas Permata Harapan School',
    fullNameLabel: 'Nama Lengkap',
    fullNamePlaceholder: 'Masukkan nama lengkap Anda',
    emailLabel: 'Alamat Email',
    emailPlaceholder: 'Masukkan alamat email Anda',
    phoneLabel: 'Nomor Telepon (Indonesia)',
    phonePlaceholder: 'Contoh: 08123456789',
    passwordLabel: 'Kata Sandi',
    passwordPlaceholder: 'Buat kata sandi yang aman (min 8 karakter)',
    confirmPasswordLabel: 'Konfirmasi Kata Sandi',
    confirmPasswordPlaceholder: 'Konfirmasi kata sandi Anda',
    registerButton: 'Buat Akun',
    registeringButton: 'Membuat Akun...',
    loginLink: 'Sudah punya akun? Masuk',
    backToHome: 'Kembali ke Beranda',
    successTitle: 'Akun Berhasil Dibuat!',
    successMessage: 'Akun Anda telah dibuat. Sekarang Anda dapat masuk untuk mengakses portal pendaftaran.',
    signInButton: 'Masuk Sekarang',
    errorTitle: 'Pendaftaran Gagal',
    passwordMismatch: 'Kata sandi tidak cocok',
    passwordTooShort: 'Kata sandi harus minimal 8 karakter',
    invalidEmail: 'Silakan masukkan alamat email yang valid',
    invalidPhone: 'Nomor telepon harus 10-13 digit (format Indonesia: 08xxx)',
    requiredFields: 'Silakan isi semua field yang diperlukan',
  },
};

function RegisterPage({ language = 'en', onLanguageChange, onNavigate }) {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const copy = translations[language];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-numeric characters
    const cleanedPhone = phone.replace(/\D/g, '');
    
    // Indonesian phone: must start with 0 (or 62) and be 10-13 digits total
    // Format: 08xx (2 digits prefix + 8-11 digits)
    const phoneRegex = /^0\d{9,12}$/;
    
    return phoneRegex.test(cleanedPhone);
  };

  const sanitizePhone = (phone) => {
    // Remove all non-numeric characters
    return phone.replace(/\D/g, '');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // For phone field, only allow numeric input
    if (name === 'phone') {
      processedValue = value.replace(/\D/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.fullName || !formData.email || !formData.phone || !formData.password || !formData.confirmPassword) {
      setError(copy.requiredFields);
      return;
    }

    // Trim and validate fullName
    const fullName = formData.fullName.trim();
    if (fullName.length < 3) {
      setError(language === 'id' ? 'Nama harus minimal 3 karakter' : 'Name must be at least 3 characters');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError(copy.invalidEmail);
      return;
    }

    if (!validatePhone(formData.phone)) {
      setError(copy.invalidPhone);
      return;
    }

    if (formData.password.length < 8) {
      setError(copy.passwordTooShort);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(copy.passwordMismatch);
      return;
    }

    setIsSubmitting(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout

      const response = await fetch('https://permataharapansecondary.sch.id/api/admissions/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: fullName,
          email: formData.email.toLowerCase(),
          phone: sanitizePhone(formData.phone),
          password: formData.password,
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);
      const data = await response.json();

      if (data.ok) {
        setSuccess(true);
      } else {
        setError(data.message || 'Registration failed. Please try again.');
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        setError('Request timeout. Please check your connection and try again.');
      } else {
        setError('Network error. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  const handleSignIn = () => {
    if (onNavigate) {
      onNavigate('login');
    } else {
      window.location.hash = '#login';
      window.location.reload();
    }
  };

  if (success) {
    return (
      <div className="register-page">
        <div className="register-container">
          <div className="register-success">
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22,4 12,14.01 9,11.01"/>
              </svg>
            </div>
            <h2>{copy.successTitle}</h2>
            <p>{copy.successMessage}</p>
            <div className="success-actions">
              <button type="button" className="btn-secondary" onClick={handleBackToHome}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                {copy.backToHome}
              </button>
              <button type="button" className="btn-primary" onClick={handleSignIn}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                  <polyline points="10 17 15 12 10 7"/>
                  <line x1="15" y1="12" x2="3" y2="12"/>
                </svg>
                {copy.signInButton}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="register-page">
      {/* Back Button Fixed Position */}
      <button type="button" className="back-button-fixed" onClick={handleBackToHome}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5"/>
          <path d="M12 19l-7-7 7-7"/>
        </svg>
        <span>{copy.backToHome}</span>
      </button>

      {/* Floating Error Toast */}
      {error && (
        <div className="error-toast-modern">
          <div className="error-toast-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
          </div>
          <div className="error-toast-text">
            <strong>{copy.errorTitle}</strong>
            <p>{error}</p>
          </div>
          <button type="button" className="error-toast-close" onClick={() => setError('')}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      )}

      <div className="register-container">
        <div className="register-header">
          <div className="register-brand">
            <img src="/logo_phs.png" alt="PHS Logo" className="register-logo" />
            <div className="register-brand-text">
              <h1>{copy.pageTitle}</h1>
              <p>{copy.pageSubtitle}</p>
            </div>
          </div>
        </div>

        <form className="register-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="fullName">{copy.fullNameLabel} *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                className="form-input"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder={copy.fullNamePlaceholder}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">{copy.emailLabel} *</label>
              <input
                type="email"
                id="email"
                name="email"
                className="form-input"
                value={formData.email}
                onChange={handleInputChange}
                placeholder={copy.emailPlaceholder}
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="phone">{copy.phoneLabel} *</label>
              <input
                type="tel"
                id="phone"
                name="phone"
                className="form-input"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder={copy.phonePlaceholder}
                maxLength="13"
                required
                disabled={isSubmitting}
                pattern="[0-9]*"
                inputMode="numeric"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">{copy.passwordLabel} *</label>
              <input
                type="password"
                id="password"
                name="password"
                className="form-input"
                value={formData.password}
                onChange={handleInputChange}
                placeholder={copy.passwordPlaceholder}
                required
                disabled={isSubmitting}
                minLength="8"
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">{copy.confirmPasswordLabel} *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                className="form-input"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                placeholder={copy.confirmPasswordPlaceholder}
                required
                disabled={isSubmitting}
                minLength="8"
              />
            </div>
          </div>

          <button 
            type="submit" 
            className="register-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <svg className="spinner" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="3"/>
                </svg>
                {copy.registeringButton}
              </>
            ) : (
              copy.registerButton
            )}
          </button>

          <div className="register-footer">
            <button type="button" className="login-link" onClick={handleSignIn}>
              {copy.loginLink}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterPage;
